import styled from '@emotion/styled';
import type { fabric } from 'fabric';
import { useFabricEditor } from 'modules/fabric-editor';
import { selectActiveTemplate } from 'modules/manager/store';
import React, { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from 'store/hooks';
import TemplateInfo from './template-info';
import { FallbackInspector, getInspector } from './inspectors';
import AlignmentSection from './inspectors/common/alignment-section';
import LayerSection from './inspectors/common/layer-section';
import StyleClipboardSection from './inspectors/common/style-clipboard-section';
import MixedInspector from './inspectors/mixed-inspector';
import LayoutBlocksPanel from './layout-blocks-panel';
import CommonMatchLayoutSection from './inspectors/common/common-match-layout-section';

const EditorRightToolbar: React.FC = () => {
  const editor = useFabricEditor();
  const template = useAppSelector(selectActiveTemplate);
  const [active, setActive] = useState<fabric.Object[]>([]);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    const offSel = editor.on('selection:changed', (e) => setActive(e.active));
    const offZoom = editor.on('zoom:changed', (e) => setZoom(e.zoom));
    return () => {
      offSel();
      offZoom();
    };
  }, [editor]);

  const kinds = useMemo(
    () => new Set(active.map((o) => (o.data as { kind?: string } | undefined)?.kind ?? o.type)),
    [active],
  );

  return (
    <Root>
      <SectionTitle>Инспектор</SectionTitle>
      {renderBody()}
    </Root>
  );

  function renderBody() {
    if (active.length === 0) {
      return (
        <NoSelection>
          <TemplateInfo
            name={template?.name}
            version={template?.version}
            objectCount={template?.objects?.length ?? 0}
            zoom={zoom}
          />
          <LayoutBlocksPanel editor={editor} />
          <CommonMatchLayoutSection editor={editor} />
          <StyleClipboardSection editor={editor} active={active} />
        </NoSelection>
      );
    }

    const homogeneous = kinds.size === 1;
    const kind = homogeneous ? kinds.values().next().value : undefined;
    const Inspector = homogeneous ? getInspector(kind) ?? FallbackInspector : MixedInspector;

    return (
      <InspectorBody>
        <Row>
          {homogeneous ? (
            <>
              <Label>Тип</Label>
              <Value>{kind ?? 'unknown'}</Value>
            </>
          ) : (
            <>
              <Label>Выбрано</Label>
              <Value>{active.length}</Value>
            </>
          )}
        </Row>
        <Inspector objects={active} editor={editor} />
        <AlignmentSection editor={editor} active={active} />
        <StyleClipboardSection editor={editor} active={active} />
        <LayerSection objects={active} editor={editor} />
      </InspectorBody>
    );
  }
};

export default React.memo(EditorRightToolbar);

const Root = styled.div`
  width: 308px;
  height: 100%;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-left: 1px solid #e5e7eb;
  background: #ffffff;
  overflow-y: auto;
`;

const SectionTitle = styled.h3`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
  margin: 0;
`;

const InspectorBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const NoSelection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
`;

const Label = styled.span`
  color: #6b7280;
`;

const Value = styled.span`
  color: #111827;
  font-weight: 500;
`;
